import { useCallback, useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { TextSelect } from "@/react/components/shared/TextSelect";
import { Form } from "@/react/components/shared/uikit/Form";
import { useBuilderStore } from "@circle-react-shared/BuilderUI";
import type { NestedStyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { mergeStylesPreserving } from "@circle-react-shared/Emails/VisualEmailBuilder/Utils/mergeStylesPreserving";
import { PARAGRAPH_DEFAULT_DATA, TEXT_SELECT_OPTIONS } from "../constants";
import type { ParagraphStyle } from "../constants";

export const isParagraphBlock = (value: string): boolean => value === "p";

export const ParagraphLevelPicker = () => {
  const data = useBuilderStore(state => state.getSelectedBlock());
  const selectedBlock = data?.block;
  const updateSelectedBlock = useBuilderStore(
    state => state.updateSelectedBlock,
  );

  const isParagraphBlockType = useMemo(
    () => !!selectedBlock && isParagraphBlock(selectedBlock.type),
    [selectedBlock],
  );

  const handleParagraphStyleChange = useCallback(
    (paragraphStyle: ParagraphStyle) => {
      if (!selectedBlock) return;

      const paragraphStyleData = PARAGRAPH_DEFAULT_DATA[paragraphStyle];

      const currentStylesValue = selectedBlock.props.styles?.value as
        | NestedStyleValue
        | undefined;
      const defaultStylesValue = paragraphStyleData.props.styles?.value as
        | NestedStyleValue
        | undefined;
      const mergedStylesValue = mergeStylesPreserving({
        current: currentStylesValue,
        defaults: defaultStylesValue,
        preserveKeys: ["margin", "padding", "textAlign"],
      });

      updateSelectedBlock({
        ...selectedBlock,
        props: {
          ...selectedBlock.props,
          paragraphStyle: {
            prop: "other",
            type: "paragraph-style-picker",
            value: paragraphStyle,
          },
          styles: {
            prop: "styles" as const,
            type: "styles" as const,
            value: mergedStylesValue,
          },
        },
      });
    },
    [selectedBlock, updateSelectedBlock],
  );

  if (!isParagraphBlockType || !selectedBlock) return null;

  const currentParagraphStyle =
    (selectedBlock.props.paragraphStyle?.value as ParagraphStyle) || "p1";

  return (
    <div className="py-3">
      <Form.Item
        name="Paragraph Style"
        label={t("builder_ui.prop_type.paragraph_style")}
        hideDescription
        hideBorder
        labelClassName="text-dark text-xs font-normal"
        className="!p-0"
      >
        <TextSelect
          options={TEXT_SELECT_OPTIONS}
          value={currentParagraphStyle}
          onChange={handleParagraphStyleChange}
          placeholder={t("builder_ui.prop_type.paragraph_style")}
          buttonClassName="w-full"
        />
      </Form.Item>
    </div>
  );
};
